import { Product } from '@/models/Product';
import { Component, Prop, Ref, Vue, Watch } from 'vue-property-decorator';
import ErrorHandler from '@/support/ErrorHandler';
import { Size } from '@/models/Size';
import { Color } from '@/models/Color';
import { cloneDeep, debounce, isEqual, size, uniqBy } from 'lodash';
import { fileToBase64 } from '@/support/String';
import MiFileUpload from '@/components/mi-file-upload/MiFileUpload';
import AttributeSelector from '@/components/AttributeSelector/AttributeSelector';
import { Supplier } from '@/models/Supplier';
import { Brand } from '@/models/Brand';
import { Category } from '@/models/Category';
import { Season } from '@/models/Season';
import { AttributeOption } from '@/models/AttributeOption';
import { ImageAction } from '@/components/ImageCollection/ImageCollection';
import { ProductVariant } from '@/models/ProductVariant';
import { Tag } from '@/models/Tag';
import {Attribute} from "@/models/Attribute";
import {AxiosError} from "axios";
import { PurchaseOrder } from '@/models/PurchaseOrder';
import { SupplierSizePack } from '@/models/SupplierSizePack';
import { SupplierSize } from '@/models/SupplierSize';
import Client from '@/support/Client';

@Component<QuickAddProductsDialog>({
  components: {
    AttributeSelector
  }
})
export default class QuickAddProductsDialog extends Vue {
  //#region @Props
  /**
  * ie.
  * @Prop()
  * protected user!: User
  */

  @Prop()
  protected product!: Product;

  @Prop()
  protected variant!: ProductVariant;

  @Prop({ default: undefined })
  protected purchaseOrder!: PurchaseOrder;

  //#endregion


  //#region @Refs
  /**
  * ie.
  * @Ref()
  * readonly anotherComponent!: AnotherComponent
  */

  @Ref() readonly fileUpload!: MiFileUpload;
  //#endregion


  //#region Class properties
  /**
  * ie.
  * protected isLoading = true;
  * * protected company: Company | null = null;
  */

  protected isLoading = true;
  protected currentStep = 1;

  protected selectedColors: Color[] = [];
  protected selectedSizes: Size[] = [];
  protected selectedSupplierColors: string[] = [];
  protected selectedSupplierSizes: SupplierSize[] = [];

  protected attributes: Attribute[] = [];
  protected activeAttribute = '';
  protected selectedAttributes: attributeItem[] = [];

  protected acceptedFileTypes = [
    'image/png',
    'image/jpeg',
    'image/svg+xml',
  ];
  protected uploadedFiles: UploadedFile[] = [];
  protected selectedFile: UploadedFile = {};
  protected previewFiles: previewFile[] = [];

  protected variants: Variant[] = [];
  protected internalVariant: Variant = {
    buy_price: '',
    retail_price: '',
    size: null,
    supplier_size: null,
    color: null,
    supplier_color: '',
    upc: '*',
    is_dummy_upc: false,
    season: null,
    tags: null,
    block_date: '',
    final_return_date: '',
    minimum_stock_quantity: '',
    is_rerun: false,
    ...this.purchaseOrder
      ? {
        quantity: 0,
      }
      : {},
  };

  protected internalProduct: Product = new Product();

  protected categories: Category[] | null = null;
  protected categorySearch: string = "";
  protected searchCategoryDebounce: Function = debounce((searchInput: string) => this.getCategories(searchInput), 400);

  protected selectedProduct: null | Product = null;
  protected isCreatingNewProduct = true;

  protected selectedSupplierSizePacksPrevious: SupplierSizePack[] = [];
  protected selectedSupplierSizePacks: SupplierSizePack[] = [];
  protected selectedTotalStyleOrderQuantity = null;

  protected toggleAllDummyUpc = false;

  protected toggleAllIsRerun = false;

  //#endregion


  //#region Lifecycle Hooks / Init
  /**
  * ie.
  * protected mounted(): void {
  *  console.log('MyComponent has Mounted');
  * }
  */

  protected mounted(): void {
    if (this.product) {
      this.selectedProduct = this.product;
      this.isCreatingNewProduct = false;
    }

    this.initialize();
  }
  //#endregion


  //#region Class methods
  /**
  * ie.
  * protected toggleLoading(): void {
  *  this.isLoading = ! this.isLoading;
  * }
  */

  protected async initialize(): Promise<void> {
    this.isLoading = true;

    // PO -> Quick add products

    if (this.purchaseOrder) {
      if (typeof this.purchaseOrder.supplier === 'object') {
        this.internalProduct.supplier = this.purchaseOrder.supplier;
      }
      if (typeof this.purchaseOrder.brand === 'object') {
        this.internalProduct.brand = this.purchaseOrder.brand;
      }
    }

    // Product edit

    if (this.hasSelectedProduct && this.selectedProduct !== null) {
      this.internalProduct = cloneDeep(this.selectedProduct);
    }

    // Variant edit
    if (this.variant) {
      this.parseInternalVariant();
    }

    await this.getAttributes();

    this.prefillAttributesWhenCloningVariant();

    this.isLoading = false;
  }

  protected parseInternalVariant() {
    this.internalVariant = {
      buy_price: `${this.variant.buy_price}`,
      retail_price: `${this.variant.retail_price}`,
      size: (this.variant.size as Size),
      supplier_size: (this.variant.supplier_size as SupplierSize),
      color: (this.variant.color as Color),
      supplier_color: this.variant.supplier_color,
      upc: this.variant.upc || '',
      is_dummy_upc: !!this.variant.is_dummy_upc,
      season: (this.variant.season as Season),
      tags: (this.variant.tags as Tag[]),
      block_date: `${this.variant.block_date}`,
      final_return_date: `${this.variant.final_return_date}`,
      minimum_stock_quantity: `${this.variant.minimum_stock_quantity}`,
      is_rerun: !!this.variant.is_rerun,
      ...this.purchaseOrder
        ? {
          quantity: 0,
        }
        : {},
    }
    this.selectedSizes = this.internalVariant.size ? [this.internalVariant.size] : [];
    this.selectedColors = this.internalVariant.color ? [this.internalVariant.color] : [];
  }

  protected async createVariants(): Promise<void> {
    // Complex logic
    // For each color, combine with a size, and also combine with supplier size
    this.selectedColors.forEach((selectedColor) => {
      this.internalVariant.color = selectedColor;
      // colors * sizes

      this.selectedSizes.forEach((selectedSize) => {
        this.internalVariant.size = selectedSize;
        this.variants.push(cloneDeep(this.internalVariant));
        this.internalVariant.size = null;
      });
      // colors * supplier_sizes
      this.selectedSupplierSizes.forEach((selectedSize) => {
        this.internalVariant.supplier_size = selectedSize;
        this.variants.push(cloneDeep(this.internalVariant));
        this.internalVariant.supplier_size = null;
      });

      this.internalVariant.color = null;
    });

    // Then for each supplier color, combine with a size, and also combine with a supplier size
    this.selectedSupplierColors.forEach(async (selectedColor) => {
      this.internalVariant.supplier_color = selectedColor;
      // supplier_colors * sizes
      this.selectedSizes.forEach((selectedSize) => {
        this.internalVariant.size = selectedSize;
        this.variants.push(cloneDeep(this.internalVariant));
        this.internalVariant.size = null;
      });
      // supplier_colors * supplier_sizes
      this.selectedSupplierSizes.forEach((selectedSize) => {
        this.internalVariant.supplier_size = selectedSize;
        this.variants.push(cloneDeep(this.internalVariant));
        this.internalVariant.supplier_size = null;
      });

      this.internalVariant.supplier_color = "";
    });

    this.variants = [
      ...this.decorateVariantsWithQuantity(
        await this.decorateVariantsWithSize(this.variants)
      )
    ];
  }

  protected get brandId(): string {
    return this.internalProduct.brand
      && this.internalProduct.brand.id
        ? this.internalProduct.brand.id
        : '';
  }

  protected decorateVariantsWithQuantity(variants: Variant[]): Variant[] {
    if (this.selectedTotalStyleOrderQuantity === null) {
      return variants;
    }

    const sumDefaultOrderQuantities = variants.reduce((accumulator: number, variant: Variant) => accumulator + (variant.supplier_size && variant.supplier_size.default_order_quantity || 1), 0);

    const amountAddIterations = Math.floor((this.selectedTotalStyleOrderQuantity || 1) / sumDefaultOrderQuantities);

    return variants.map((variant) => {
      if (! variant.supplier_size) { return variant; }

      variant.quantity = (variant.supplier_size.default_order_quantity || 1) * amountAddIterations;

      return variant;
    });
  }

  // Decorate variants with size if they have supplier size and there is a brand selected
  protected async decorateVariantsWithSize(variants: Variant[]): Promise<Variant[]> {
    // Early return if no brand selected
    if (! this.brandId) { return variants; }

    let index = 0;
    for (let variant of variants) {
      if (variant.supplier_size && variant.supplier_size.id) {
        const size = await this.getDeviation(this.brandId, variant.supplier_size.id);
        if (size) {
          variants[index].size = size;
        }
      }

      index++;
    }

    return variants;
  }

  protected prefillAttributesWhenCloningVariant(): void {
    if (!this.variant || !this.variant.attributes) { return; }

    // id's on this.variant.attributes are different from this.attributes
    // - so we first get the titles from variant.attributes
    const simpleAttributes = this.variant.attributes.map((attribute: Attribute): { title: string, valueTitle: string } => {
      const selectedOption = attribute.options ? attribute.options.find((option: AttributeOption): boolean => option.id === attribute.value) : undefined;

      return {
        title: attribute.title || '',
        valueTitle: selectedOption && selectedOption.title ? selectedOption.title : '',
      }
    });

    // - and then we use the titles to find the corresponding ids in this.attributes
    const selectedAttributes = simpleAttributes.map((simpleAttribute): attributeItem => {
      const attribute = this.attributes.find((attribute) => attribute.title === simpleAttribute.title);
      const option = attribute && attribute.options ? attribute.options.find((option) => option.title === simpleAttribute.valueTitle) : undefined;

      return {
        attribute: attribute ? attribute.id : '',
        option: option ? option.id : '',
      }
    });

    this.selectedAttributes = selectedAttributes;
  }

  protected close(): void {
    this.$emit('input', false);
  }

  protected onChangeToggleAllDummyUpc(value: boolean) {
    this.variants.map((variant: Variant) => variant.is_dummy_upc = value);
  }

  protected onChangeToggleAllIsRerun(value: boolean) {
    this.variants.map((variant: Variant) => variant.is_rerun = value);
  }

  protected handleItemDropped(file: File) {
    if(!file) {
      return;
    }

    fileToBase64(file).then((encodedFile: string) => {
      const uploadedFile: UploadedFile = {
        url: encodedFile,
        name: file.name,
        file: file,
      };

      const previewFile: previewFile = {
        url_large: encodedFile,
      };
      this.previewFiles.push(previewFile);
      this.uploadedFiles.push(uploadedFile);
    });

    const fileUploadComponent = this.fileUpload;
    fileUploadComponent.uploaded();
  }

  protected addVariant() {
    const lastVariant = this.variants[this.variants.length - 1];
    const variant = lastVariant ? lastVariant : {
      buy_price: '',
      retail_price: '',
      size: null,
      supplier_size: null,
      color: null,
      supplier_color: '',
      upc: '*',
      is_dummy_upc: false,
      season: null,
      tags: null,
      block_date: '',
      final_return_date: '',
      is_rerun: false,
      minimum_stock_quantity: '',
      ...this.purchaseOrder
        ? {
          quantity: 0,
        }
        : {},
    };

    this.variants.push({...variant});
  }

  protected attributeOptionsChanged(attributeOptions: AttributeOption[]) {
    this.selectedAttributes = attributeOptions.map(attributeOption => {
      return {
        attribute: attributeOption.attribute || '',
        option: attributeOption.id || '',
      }
    })
  }

  protected async onChangeVariantValue(value: any, key: string, variantIndex: number): Promise<void> {
    (this.variants[variantIndex] as {[key: string]: any})[key] = value;

    if (key === 'supplier_size') {
      this.onChangeVariantSupplierSize(value, variantIndex);
    }
  }

  protected async onChangeVariantSupplierSize(supplierSize: SupplierSize, variantIndex: number): Promise<void> {
     // If not enough data to query size based on supplierSize
      if (
        ! this.brandId
        || ! supplierSize
        || ! supplierSize.id
      ) {

        // Clear size
        this.variants[variantIndex].size = null;

        return;
      }

      // Fetch size from supplierSize
      const size = await this.getDeviation(this.brandId, supplierSize.id);
      if (size) {
        this.variants[variantIndex].size = size;
      }
  }

  protected getSupplierSizesFromSupplierSizePacks(supplierSizePacks: SupplierSizePack[]): SupplierSize[] {
    const supplierSizesIncludingDuplicates: SupplierSize[] = supplierSizePacks.map((supplierSizePack) => supplierSizePack.sizes || []).flat();

    const supplierSizes: SupplierSize[] = uniqBy(supplierSizesIncludingDuplicates, 'id');

    return supplierSizes;
  }

  /*
  ** Scenario:
  ** It's possible to select/deselect sizes through: SupplierSizePackAutocomplete + SupplierSizeAutocomplete
  ** One might first select a size through the SupplierSizeAutocomplete
  ** After that one might select/deselect sizes through the SupplierSizePackAutocomplete
  **
  ** Solution:
  ** When selecting/deselecting items in the SupplierSizePackAutocomplete, it will only select/deselect what's part of that pack and leave the rest of what's selected as is
  */
  protected selectSupplierSizes(sizePacks: SupplierSizePack[], sizePacksPrevious: SupplierSizePack[], sizesCurrently: SupplierSize[]): SupplierSize[] {
    // Create a list of sizes to add
    const sizesFromSizePacks: SupplierSize[] = this.getSupplierSizesFromSupplierSizePacks(sizePacks);

    // Add the FromSizePack and remove duplicates
    const sizesCombined: SupplierSize[] = uniqBy([...sizesCurrently, ...sizesFromSizePacks], 'id');

    // Create a list of previously selected sizes
    const sizesFromSizePacksPrevious: SupplierSize[] = this.getSupplierSizesFromSupplierSizePacks(sizePacksPrevious);

    // Get id's from sizes
    const sizeIdsFromSizePacks: string[] = sizesFromSizePacks.map((size) => size.id || '');

    // Create a list of which sizes that were deselected through sthe size packs autocomplete
    const deselectedSizesFromSizePacks: SupplierSize[] = sizesFromSizePacksPrevious.filter((sizeInPreviousSizePack) => ! sizeIdsFromSizePacks.includes(sizeInPreviousSizePack.id || ''));

    // Get id's from deselected sizes
    const deselectedSizeIdsFromSizePacks: string[] = deselectedSizesFromSizePacks.map((size) => size.id || '');

    // Remove what was deselected through the size packs autocomplete
    const sizesCombinedExclDeselectedFromSizePacks: SupplierSize[] = sizesCombined.filter((sizeCombined) => ! deselectedSizeIdsFromSizePacks.includes(sizeCombined.id || ''));

    return sizesCombinedExclDeselectedFromSizePacks;
  }

  protected onSupplierSizePackInput(sizePacks: SupplierSizePack[]): void {
    // ASelectInput fires the 'input' event multiple times, we only want to apply changes if there's a change
    if (isEqual(sizePacks, this.selectedSupplierSizePacksPrevious)) { return; }

    this.selectedSupplierSizes = this.selectSupplierSizes(sizePacks, this.selectedSupplierSizePacksPrevious, this.selectedSupplierSizes);

    this.selectedSupplierSizePacksPrevious = sizePacks;
  }

  protected requestClient(method: string, url: string, payload: any = {}): Promise<any> {
    return Client(method, url, payload).then(
      (response: object) => {
        return Promise.resolve(response);
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );
  }

  protected async getDeviation(brandId: string, supplierSizeIds: string): Promise<Size|null> {
    // Query sizes
    const sizeSearchResult = await this.requestClient(
      'get',
      `/supplier-size-get-deviation/brand/${brandId}/supplier-size/${supplierSizeIds}`,
    )
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
        return null;
      });

    // Early return if no results
    if (! sizeSearchResult || ! sizeSearchResult.data) { return null; }

    return sizeSearchResult.data;
  }

  @Watch('isCreatingNewProduct')
  protected isCreatingNewProductChanged(newState: boolean, oldState: boolean): void {
    if (newState === oldState) { return; }

    this.selectedProduct = this.isCreatingNewProduct || ! this.product ? null : this.product;
    this.internalProduct = this.isCreatingNewProduct || ! this.product ? new Product() : this.product;

    this.initialize();
  }
  //#endregion


  //#region Async methods
  /**
  * ie.
  * protected async fetchUserCompany(): Promise<void> {
  *  this.company = await new Company().filter({user: this.user.id}).all();
  * }
  */

  protected async create(): Promise<void> {
    this.isLoading = true;

    try {
      const product: Product = await new Product()
        .quickAdd()
        .create(this.payload)

      const filePromises = this.uploadedFiles.map((image, index) => this.attachImage(image, product, index === 0));
      await Promise.all(filePromises);

      this.$emit('created');
      this.close();
    } catch (error) {
      ErrorHandler.network(error);
    }

    this.isLoading = false;
  }

  protected async attachImage(image: UploadedFile, product: Product, isDefault: boolean): Promise<any> {
    return await new Product().attachImage(product.id).create({image: image.url , default: isDefault});
  }

  protected toggleAddAttributeOption(attribute: string, option: string) {
    if (this.isOptionSelected(attribute, option)) {
      const clearedAttributes = this.selectedAttributes.filter((currentAttribute: attributeItem) => {
        return (currentAttribute.attribute !== attribute) || (currentAttribute.attribute === attribute && currentAttribute.option !== option);
      });

      this.$set(this, 'selectedAttributes', clearedAttributes);
    } else {
      const attributeFilter: attributeItem = { attribute, option };
      this.selectedAttributes.push(attributeFilter);
    }
  }

  protected isOptionSelected(attribute: string, option: string) {
    return this.selectedAttributes.some((currentAttribute: attributeItem) => {
      return currentAttribute.attribute === attribute && currentAttribute.option === option;
    });
  }

  protected async getAttributes(): Promise<void> {
    await new Attribute().include('options')
      .limit(100)
      .all()
      .then((attributes: Attribute[]) => {
        this.attributes = attributes;
      })
      .catch((error: AxiosError) => {
      });
  }

  protected async getCategories(filter: string = ""): Promise<void> {
    await new Category()
      .filter("search", filter)
      .limit(999) // so we can also search inside all the sub-items
      .all()
      .then((categories: Category[]) => {
        let parsedCategories: Category[] = [];
        categories.forEach((category: any) => {
          parsedCategories.push(category);
          if (category.sub_categories && category.sub_categories.length > 1) {
            category.sub_categories.forEach((subColor: Category) => {
              parsedCategories.push(subColor);
            });
          }
        });

        this.categories = parsedCategories;
      })
      .catch((error: AxiosError) => {});
  }

  //#endregion


  //#region Getters & Setters
  /**
  * ie.
  * protected get companyName(): string {
  *  return this?.company.name || 'N/A';
  * }
  */

  protected get supplierSizeModel(): SupplierSize {
    return SupplierSize.getInstance(new SupplierSize().limit(999));
  }

  protected get sizeModel(): Size {
    return Size.getInstance(new Size().limit(999));
  }

  protected get colorModel(): Color {
    return Color.getInstance(new Color().limit(50));
  }

  protected get supplierModel(): Supplier {
    return Supplier.getInstance(new Supplier().limit(50));
  }

  protected get brandModel(): Brand {
    return Brand.getInstance(new Brand().limit(50));
  }

  protected get categoryModel(): Category {
    return Category.getInstance(new Category().limit(50));
  }

  protected get seasonModel(): Season {
    return Season.getInstance(new Season().limit(50));
  }

  protected get tagModel(): Tag {
    return Tag.getInstance(new Tag().limit(50));
  }

  protected get productModel(): Product {
    return Product.getInstance(new Product().include(['brand', 'supplier', 'categories']).limit(50));
  }

  protected get payload(): ProductPayload {
    const payload: ProductPayload = {
      ...this.variant && this.variant.id ? { clone: this.variant.id } : {},
      brand: this.internalProduct && this.internalProduct.brand ? (this.internalProduct.brand as Brand).id || '' : '',
      supplier: this.internalProduct && this.internalProduct.supplier ? (this.internalProduct.supplier as Supplier).id || '' : '',
      supplier_style_name: this.internalProduct.supplier_style_name,
      supplier_style_code: this.internalProduct.supplier_style_code,
      description: this.internalProduct.description,
      categories: this.internalProduct.categories && this.internalProduct.categories.length ? this.internalProduct.categories.map(category => category.id) : [],
      ...this.purchaseOrder
        ? {
          purchase_order: this.purchaseOrder && typeof this.purchaseOrder === 'object'
            ? this.purchaseOrder.id
            : this.purchaseOrder,
        }
        : {},
      variants: this.variants.map(variant => {
        return {
          color: variant.color && variant.color.id ? variant.color.id : '',
          size: variant.size && variant.size.id ? variant.size.id : '',
          supplier_size: variant.supplier_size && variant.supplier_size.id ? variant.supplier_size.id : '',
          supplier_color: variant.supplier_color,
          season: variant.season && variant.season.id ? variant.season.id : '',
          tags: variant.tags && variant.tags.length ? variant.tags.map(tag => tag.id || '') : [],
          buy_price: variant.buy_price,
          retail_price: variant.retail_price,
          block_date: variant.block_date,
          final_return_date: variant.final_return_date,
          is_rerun: variant.is_rerun,
          minimum_stock_quantity: variant.minimum_stock_quantity,
          upc: variant.upc && variant.upc !== '*' ? variant.upc : '',
          is_dummy_upc: variant.is_dummy_upc,
          ...this.purchaseOrder
            ? {
              quantity: variant.quantity,
            }
            : {},
          attributes: this.selectedAttributes,
        }
      }),
    }

    // By sending a product is you can add variants to that product
    if (this.product) {
      payload.product = this.product.id;
    }

    return payload;
  }

  protected get imageActions(): ImageAction[] {
    return [
      {
        click: (image, index) => {
          if (!image || index === undefined || index < 0) {
            return;
          }

          this.uploadedFiles.splice(index, 1);
          this.previewFiles.splice(index, 1);
        },
        icon: 'delete',
        class: 'delete',
      },
    ]
  }

  protected get steps(): Step[] {
    return [
      {
        title: 'Product',
      },
      {
        title: 'Variants',
      },
      {
        title: "Foto's",
      },
      {
        title: 'Attributen',
      },
      {
        title: 'Overzicht',
      },
    ];
  }

  protected get hasSelectedProduct(): boolean {
    return ! this.isCreatingNewProduct && !! this.selectedProduct;
  }

  protected get totalQuantity(): number {
    return this.variants.reduce((accumulator: number, currentValue: Variant): number => Number(accumulator) + (Number(currentValue.quantity) || 0), 0);
  }

  protected get calculatedMargin(): string {
    if ( !this.internalVariant.buy_price || ! this.internalVariant.retail_price) { return ''; }

    const buyPrice = Number(`${this.internalVariant.buy_price}`.replace(',', '.'));
    const retailPrice = Number(`${this.internalVariant.retail_price}`.replace(',', '.'));

    if (isNaN(buyPrice) || isNaN(retailPrice)) { return ''; }

    console.log('buyPrice', buyPrice);
    console.log('retailPrice', retailPrice);

    return (
      (
        (
          retailPrice / 1.21
        )
        - buyPrice
      )
      / (
        retailPrice / 1.21
      )
    ).toFixed(2);
  }

  //#endregion


  //#region @Watchers
  /**
  * ie.
  * @Watch('company')
  * protected companyChanged() {
  *  console.log('Company Changed') ;
  * }
  */

  @Watch('currentStep')
  protected stepsChanged() {
    if (this.currentStep === 2) {
      this.variants = [];
    }

    if (this.currentStep === 3) {
      this.activeAttribute = '0';
    }
  }

  @Watch("categorySearch")
  protected categorySearchChanged(category: string) {
    this.searchCategoryDebounce(category);
  }

  //#endregion
}

interface UploadedFile {
  url?: string;
  name?: string;
  file?: any;
}

interface previewFile {
  url_large?: string;
  name?: string;
}

interface Variant {
  buy_price: string;
  retail_price: string;
  size: Size | null;
  supplier_size: SupplierSize | null;
  color: Color | null;
  supplier_color: string;
  upc: string;
  is_dummy_upc: boolean,
  season: Season | null;
  tags: Tag[] | null;
  block_date: string;
  final_return_date: string;
  is_rerun: boolean;
  minimum_stock_quantity: string;
  quantity?: number;
}

interface ProductPayload {
  brand: string;
  supplier: string;
  supplier_style_name: string;
  supplier_style_code: string;
  description: string;
  categories: string[];
  variants: VariantPayload[];
  product?: string;
}

interface VariantPayload {
  color: string;
  size: string;
  supplier_size: string;
  supplier_color: string;
  season: string;
  buy_price: string;
  retail_price: string;
  block_date: string;
  final_return_date: string;
  is_rerun: boolean;
  tags: string[];
  minimum_stock_quantity: string;
  upc: string;
  is_dummy_upc: boolean;
  attributes: attributeItem[];
  quantity?: number;
}

interface Step {
  title: string;
}

interface attributeItem {
  attribute?: string;
  option?: string;
}
